import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
  ReactNode,
} from 'react';
import {
  ExpandedState,
  Row,
} from '@tanstack/react-table';
import IconSystem from 'icon-collection/icon-systems';
import { SyutGrid, type SyutGridColumn } from '../SyutGrid';
import { type ActionMenuItem } from '../ActionMenu';

// ============================================================================
// INJECT SELF-CONTAINED STYLES (HMR-safe)
// ============================================================================

if (typeof document !== 'undefined') {
  const styleId = 'syut-tree-grid-styles';
  let styleEl = document.getElementById(styleId) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = styleId;
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = `
    .stg-tree-cell {
      display: flex;
      align-items: center;
      white-space: nowrap;
    }

    .stg-connector {
      display: inline-flex;
      width: 1.25rem;
      flex-shrink: 0;
      position: relative;
      align-self: stretch;
      min-height: 1.5rem;
    }

    /* Vertical pass-through line (ancestor is NOT last child) */
    .stg-connector-pass::before {
      content: '';
      position: absolute;
      left: 50%;
      top: 0;
      bottom: 0;
      width: 1px;
      background: var(--tree-line-color, var(--border-color, #cbd5e1));
    }

    /* Mid-child: vertical line top-to-bottom + horizontal branch */
    .stg-connector-mid::before {
      content: '';
      position: absolute;
      left: 50%;
      top: 0;
      bottom: 0;
      width: 1px;
      background: var(--tree-line-color, var(--border-color, #cbd5e1));
    }
    .stg-connector-mid::after {
      content: '';
      position: absolute;
      left: 50%;
      top: 50%;
      width: 50%;
      height: 1px;
      background: var(--tree-line-color, var(--border-color, #cbd5e1));
    }

    /* Last child: vertical line top-to-center + horizontal branch (L-shape) */
    .stg-connector-last::before {
      content: '';
      position: absolute;
      left: 50%;
      top: 0;
      height: 50%;
      width: 1px;
      background: var(--tree-line-color, var(--border-color, #cbd5e1));
    }
    .stg-connector-last::after {
      content: '';
      position: absolute;
      left: 50%;
      top: 50%;
      width: 50%;
      height: 1px;
      background: var(--tree-line-color, var(--border-color, #cbd5e1));
    }

    /* Blank connector: no pseudo-elements, just takes up space */
    .stg-connector-blank {
      /* inherits width from .stg-connector */
    }

    .stg-chevron {
      background: none;
      border: none;
      cursor: pointer;
      padding: 2px;
      display: inline-flex;
      transition: transform 0.2s ease;
      color: var(--text-muted, #94a3b8);
      flex-shrink: 0;
    }
    .stg-chevron:hover {
      color: var(--text-primary, #1e293b);
    }
    .stg-chevron.expanded {
      transform: rotate(90deg);
    }

    .stg-chevron-spacer {
      display: inline-block;
      width: 18px;
      flex-shrink: 0;
    }

    .stg-node-icon {
      display: inline-flex;
      margin-right: 6px;
      color: var(--text-muted);
      flex-shrink: 0;
    }
    .stg-node-icon.is-folder {
      color: var(--primary, #3b82f6);
    }

    .stg-spinner {
      width: 14px;
      height: 14px;
      border: 2px solid var(--border-color, #cbd5e1);
      border-top-color: var(--primary, #3b82f6);
      border-radius: 50%;
      animation: stg-spin 0.6s linear infinite;
      margin: 0 2px;
      flex-shrink: 0;
    }
    @keyframes stg-spin {
      to { transform: rotate(360deg); }
    }
  `;
}

// ============================================================================
// TYPES
// ============================================================================

/** Constrains parentKey/sortKey to fields that are string | number | null | undefined */
type SafeKeyOf<T> = {
  [K in keyof T]: T[K] extends string | number | null | undefined ? K : never;
}[keyof T];

export interface TreeGridColumn<T> extends Omit<SyutGridColumn<T>, 'cell'> {
  /** Mark this column as the tree column — shows connectors, chevron, and node icons. */
  isTreeColumn?: boolean;
  cell?: (info: { row: Row<T>; getValue: () => any }) => ReactNode;
}

/**
 * SyutTreeGrid — Hierarchical tree table wrapping SyutGrid.
 *
 * Displays flat parent-child data as an expandable tree with connector lines,
 * folder/file icons, and chevron toggle. Built on TanStack Table's expansion model.
 *
 * Pagination paginates root-level rows. Children of expanded nodes appear regardless
 * of page size.
 *
 * @example
 * ```tsx
 * <SyutTreeGrid
 *   data={menuItems}
 *   columns={[
 *     { id: 'name', header: 'Name', accessorKey: 'name', isTreeColumn: true },
 *     { id: 'type', header: 'Type', accessorKey: 'type' },
 *   ]}
 *   parentKey="parentId"
 *   sortKey="sortOrder"
 *   defaultExpandLevel={1}
 * />
 * ```
 */
export interface SyutTreeGridProps<T extends { id: string }> {
  data: T[];
  columns: TreeGridColumn<T>[];
  parentKey?: SafeKeyOf<T>;
  sortKey?: SafeKeyOf<T>;
  getRowId?: (row: T) => string;

  // ── Tree visual options ──
  showConnectorLines?: boolean;
  showNodeIcons?: boolean;
  /** Initial expansion depth. -1=all, 0=collapsed (default), N=expand to level N. Uncontrolled only. */
  defaultExpandLevel?: number;
  /** Maximum tree depth. Default: 20. Nodes beyond this depth have no children. */
  maxDepth?: number;

  // ── Controlled expansion ──
  expanded?: ExpandedState;
  onExpandedChange?: (expanded: ExpandedState) => void;

  // ── SyutGrid pass-through ──
  rowActions?: ActionMenuItem[] | ((row: T) => ActionMenuItem[]);
  enableRowSelection?: boolean;
  onSelectionChange?: (selectedRows: T[]) => void;
  /**
   * Enable TanStack column-header sorting. Default: false.
   * When true, column sorting may reorder siblings independently.
   * Connector lines reflect sortKey order, not column-sort order.
   * Use sortKey for predictable sibling-level sort.
   */
  enableSorting?: boolean;
  enablePagination?: boolean;
  pageSizeOptions?: number[];
  defaultPageSize?: number;
  isLoading?: boolean;
  emptyMessage?: string;
  className?: string;
  visibleColumns?: string[];
  frozenLeftColumns?: string[];
  frozenRightColumns?: string[];

  // ── Lazy loading ──
  /** Return true if a row can be expanded (has lazy children not yet loaded). */
  isExpandable?: (row: T) => boolean;
  /** Set of row IDs whose children have already been loaded. */
  childrenLoadedIds?: Set<string>;
  /** Set of row IDs currently loading children. Shows spinner instead of chevron. */
  loadingNodeIds?: Set<string>;
  /** Called when a node is expanded and children need to be loaded. */
  onLoadChildren?: (rowId: string, row: T) => void;
  /** Called when any node is expanded. */
  onExpand?: (rowId: string, row: T) => void;
  /** Called when any node is collapsed. */
  onCollapse?: (rowId: string, row: T) => void;

  // ── Virtualization readiness (Phase 2) ──
  enableVirtualization?: boolean;
  estimatedRowHeight?: number;
}

// ============================================================================
// HELPERS
// ============================================================================

interface ConnectorInfo {
  isLastChild: boolean;
  parentIsLastFlags: boolean[];
}

function computeInitialExpanded<T>(
  roots: T[],
  childrenMap: Map<string, T[]>,
  level: number | undefined,
  getId: (row: T) => string,
): ExpandedState {
  if (level === -1) return true;
  if (!level || level === 0) return {};
  const result: Record<string, boolean> = {};
  const walk = (items: T[], d: number) => {
    if (d >= level!) return;
    for (const item of items) {
      const id = getId(item);
      const children = childrenMap.get(id);
      if (children?.length) {
        result[id] = true;
        walk(children, d + 1);
      }
    }
  };
  walk(roots, 0);
  return result;
}

function renderConnectors(meta: ConnectorInfo): ReactNode {
  const spans: ReactNode[] = [];
  for (let i = 0; i < meta.parentIsLastFlags.length; i++) {
    const isAncestorLast = meta.parentIsLastFlags[i];
    spans.push(
      <span
        key={`c${i}`}
        className={`stg-connector ${isAncestorLast ? 'stg-connector-blank' : 'stg-connector-pass'}`}
      />
    );
  }
  spans.push(
    <span
      key="cl"
      className={`stg-connector ${meta.isLastChild ? 'stg-connector-last' : 'stg-connector-mid'}`}
    />
  );
  return spans;
}

function renderNodeIcon<T>(row: Row<T>, canExpand: boolean): ReactNode {
  if (canExpand) {
    return (
      <span className="stg-node-icon is-folder">
        {IconSystem.folder({ size: 14 })}
      </span>
    );
  }
  return (
    <span className="stg-node-icon">
      {IconSystem.file({ size: 14 })}
    </span>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function SyutTreeGrid<T extends { id: string }>({
  data,
  columns,
  parentKey,
  sortKey,
  getRowId,
  showConnectorLines = true,
  showNodeIcons = true,
  defaultExpandLevel,
  maxDepth,
  expanded,
  onExpandedChange,
  rowActions,
  enableRowSelection,
  onSelectionChange,
  enableSorting,
  // Trees are navigated by expand/collapse — default to a continuous scrollable tree,
  // not paginated root rows (which interrupts drilling). Opt back in explicitly if needed.
  enablePagination = false,
  pageSizeOptions,
  defaultPageSize,
  isLoading,
  emptyMessage,
  className,
  visibleColumns,
  frozenLeftColumns,
  frozenRightColumns,
  isExpandable,
  childrenLoadedIds,
  loadingNodeIds,
  onLoadChildren,
  onExpand,
  onCollapse,
  enableVirtualization,
}: SyutTreeGridProps<T>) {
  // ── Phase 2 stub ──
  if (enableVirtualization && process.env.NODE_ENV === 'development') {
    console.warn('SyutTreeGrid: enableVirtualization is not implemented in Phase 1.');
  }

  // ══════════════════════════════════════════════════════════════════════════
  // 1. ID helpers + O(1) data lookup + dev-mode uniqueness check
  // ══════════════════════════════════════════════════════════════════════════

  const getIdStr = useMemo(
    () => getRowId ?? ((row: T) => row.id),
    [getRowId],
  );

  const dataMap = useMemo(() => {
    const map = new Map<string, T>();
    for (const item of data) {
      const id = getIdStr(item);
      if (process.env.NODE_ENV === 'development' && map.has(id)) {
        console.warn(
          `SyutTreeGrid: Duplicate row ID "${id}". TanStack requires unique IDs.`,
        );
      }
      map.set(id, item);
    }
    return map;
  }, [data, getIdStr]);

  // ══════════════════════════════════════════════════════════════════════════
  // 2. Build childrenMap + depthMap + rootData with cycle detection
  // ══════════════════════════════════════════════════════════════════════════

  const { rootData, childrenMap, depthMap } = useMemo(() => {
    const pKey = (parentKey ?? 'parentId') as keyof T;
    const cMap = new Map<string, T[]>();
    const dMap = new Map<string, number>();
    const limit = maxDepth ?? 20;

    // Dev-mode: validate parentKey exists on data
    if (
      process.env.NODE_ENV === 'development' &&
      data.length > 0 &&
      !(pKey in data[0])
    ) {
      console.error(
        `SyutTreeGrid: parentKey "${String(pKey)}" does not exist on data rows. ` +
          `Available keys: ${Object.keys(data[0]).join(', ')}`,
      );
    }

    // Only treat actual null/undefined/"" as no-parent
    const isNullParent = (val: unknown): boolean => val == null || val === '';

    // Null-safe comparator — nulls sort last
    const compare = sortKey
      ? (a: T, b: T) => {
          const av = a[sortKey],
            bv = b[sortKey];
          if (av == null && bv == null) return 0;
          if (av == null) return 1;
          if (bv == null) return -1;
          return av < bv ? -1 : av > bv ? 1 : 0;
        }
      : null;

    // Build allIds set + local item lookup (avoids dataMap dependency)
    const allIds = new Set<string>();
    const itemById = new Map<string, T>();
    for (const item of data) {
      const id = getIdStr(item);
      allIds.add(id);
      itemById.set(id, item);
    }

    // Group children by parent ID (no __root__ sentinel)
    for (const item of data) {
      const raw = item[pKey];
      if (isNullParent(raw) || !allIds.has(String(raw))) continue;
      const pid = String(raw);
      if (!cMap.has(pid)) cMap.set(pid, []);
      cMap.get(pid)!.push(item);
    }

    // Sort siblings
    if (compare) {
      for (const children of cMap.values()) children.sort(compare);
    }

    // Find roots — parentId is null-like OR references non-existent ID
    let roots = data.filter((item) => {
      const raw = item[pKey];
      return isNullParent(raw) || !allIds.has(String(raw));
    });
    if (compare) roots.sort(compare);

    // Compute depth per node with cycle detection
    const visited = new Set<string>();
    const cycleNodeIds = new Set<string>();
    const assignDepths = (items: T[], depth: number) => {
      for (const item of items) {
        const id = getIdStr(item);
        if (visited.has(id)) {
          cycleNodeIds.add(id);
          continue;
        }
        visited.add(id);
        dMap.set(id, depth);
        if (depth < limit) {
          const children = cMap.get(id);
          if (children) assignDepths(children, depth + 1);
        }
      }
    };
    assignDepths(roots, 0);

    // ── Disconnected cycle detection ──
    const promotedRoots: T[] = [];
    let hasUnreached = false;
    for (const item of data) {
      if (!visited.has(getIdStr(item))) {
        hasUnreached = true;
        break;
      }
    }

    if (hasUnreached || cycleNodeIds.size > 0) {
      // Handle back-edge cycles (detected during root walk)
      for (const id of cycleNodeIds) {
        if (!dMap.has(id)) {
          const item = itemById.get(id);
          if (item) {
            const parentRaw = item[pKey];
            if (!isNullParent(parentRaw)) {
              const parentId = String(parentRaw);
              const siblings = cMap.get(parentId);
              if (siblings) {
                const filtered = siblings.filter((s) => getIdStr(s) !== id);
                if (filtered.length === 0) cMap.delete(parentId);
                else cMap.set(parentId, filtered);
              }
            }
            promotedRoots.push(item);
            dMap.set(id, 0);
          }
        }
      }

      // Handle disconnected cycles: pick one per cycle to promote
      for (const item of data) {
        const id = getIdStr(item);
        if (visited.has(id)) continue;
        const parentRaw = item[pKey];
        if (!isNullParent(parentRaw)) {
          const parentId = String(parentRaw);
          const siblings = cMap.get(parentId);
          if (siblings) {
            const filtered = siblings.filter((s) => getIdStr(s) !== id);
            if (filtered.length === 0) cMap.delete(parentId);
            else cMap.set(parentId, filtered);
          }
        }
        promotedRoots.push(item);
        dMap.set(id, 0);
        assignDepths([item], 0);
      }

      if (promotedRoots.length > 0) {
        // Deduplicate: a node could already be in roots AND be cycle-detected
        const rootIdSet = new Set(roots.map((r) => getIdStr(r)));
        const uniquePromoted = promotedRoots.filter(
          (r) => !rootIdSet.has(getIdStr(r)),
        );
        if (uniquePromoted.length > 0) {
          roots = [...roots, ...uniquePromoted];
        }
      }
      if (process.env.NODE_ENV === 'development' && promotedRoots.length > 0) {
        console.warn(
          `SyutTreeGrid: Circular references detected. ${promotedRoots.length} node(s) promoted to root: ` +
            `[${promotedRoots.map((n) => getIdStr(n)).join(', ')}].`,
        );
      }
    }

    return { rootData: roots, childrenMap: cMap, depthMap: dMap };
  }, [data, parentKey, sortKey, getIdStr, maxDepth]);

  // getSubRows — enforces maxDepth via depthMap
  const getSubRowsFn = useCallback(
    (row: T): T[] | undefined => {
      const depth = depthMap.get(getIdStr(row)) ?? 0;
      if (depth >= (maxDepth ?? 20)) return undefined;
      return childrenMap.get(getIdStr(row));
    },
    [childrenMap, depthMap, getIdStr, maxDepth],
  );

  // ══════════════════════════════════════════════════════════════════════════
  // 3. Expansion state — controlled/uncontrolled
  // ══════════════════════════════════════════════════════════════════════════

  const [internalExpanded, setInternalExpanded] = useState<ExpandedState>(() =>
    computeInitialExpanded(rootData, childrenMap, defaultExpandLevel, getIdStr),
  );

  const isControlled = expanded !== undefined;
  const activeExpanded = isControlled ? expanded : internalExpanded;
  const setActiveExpanded = isControlled
    ? (val: ExpandedState) => onExpandedChange?.(val)
    : setInternalExpanded;

  // Reconcile on data change (uncontrolled only)
  const prevDataRef = useRef(data);
  useEffect(() => {
    if (isControlled || prevDataRef.current === data) return;
    prevDataRef.current = data;
    setInternalExpanded((prev) => {
      if (prev === true) return true;
      const validIds = new Set(data.map((d) => getIdStr(d)));
      const next: Record<string, boolean> = {};
      for (const [id, val] of Object.entries(prev)) {
        if (validIds.has(id)) next[id] = val;
      }
      if (defaultExpandLevel && defaultExpandLevel !== 0) {
        const fresh = computeInitialExpanded(
          rootData,
          childrenMap,
          defaultExpandLevel,
          getIdStr,
        );
        if (fresh !== true) {
          for (const [id, val] of Object.entries(
            fresh as Record<string, boolean>,
          )) {
            if (!(id in next)) next[id] = val;
          }
        }
      }
      return next;
    });
  }, [data, getIdStr, isControlled, rootData, childrenMap, defaultExpandLevel]);

  // React to defaultExpandLevel prop changes (uncontrolled only)
  const prevDefaultExpandLevelRef = useRef(defaultExpandLevel);
  useEffect(() => {
    if (isControlled) return;
    if (prevDefaultExpandLevelRef.current === defaultExpandLevel) return;
    prevDefaultExpandLevelRef.current = defaultExpandLevel;
    setInternalExpanded(
      computeInitialExpanded(rootData, childrenMap, defaultExpandLevel, getIdStr),
    );
  }, [defaultExpandLevel, isControlled, rootData, childrenMap, getIdStr]);

  // ══════════════════════════════════════════════════════════════════════════
  // 4. Expansion handler — BFS visible-first + rapid-click guard
  // ══════════════════════════════════════════════════════════════════════════

  // Refs for frequently-changing props to avoid enrichedColumns/handler recalculation
  const loadingNodeIdsRef = useRef(loadingNodeIds);
  loadingNodeIdsRef.current = loadingNodeIds;
  const isExpandableRef = useRef(isExpandable);
  isExpandableRef.current = isExpandable;

  const handleExpandedChange = useCallback(
    (newExpanded: ExpandedState) => {
      const wasAll = activeExpanded === true;
      const isAll = newExpanded === true;

      // ── Transition TO "expand all" ──
      // BFS from roots ensures visible-first loading order.
      if (isAll && !wasAll) {
        const prevObj =
          (activeExpanded as Record<string, boolean>) || {};
        let loadCount = 0;
        const LOAD_BATCH_LIMIT = 50;

        // BFS queue seeded with root-level expandable nodes
        const queue: Array<[string, T]> = [];
        for (const root of rootData) {
          const id = getIdStr(root);
          if (
            childrenMap.has(id) ||
            !!isExpandableRef.current?.(root)
          ) {
            queue.push([id, root]);
          }
        }

        // Process breadth-first — depth 0 first, then depth 1, etc.
        let qi = 0;
        while (qi < queue.length) {
          const [id, item] = queue[qi++];

          if (!prevObj[id]) {
            onExpand?.(id, item);
            if (
              onLoadChildren &&
              !childrenLoadedIds?.has(id) &&
              !loadingNodeIdsRef.current?.has(id) &&
              loadCount < LOAD_BATCH_LIMIT
            ) {
              onLoadChildren(id, item);
              loadCount++;
            }
          }

          // Enqueue expandable children for next depth level
          const children = childrenMap.get(id);
          if (children) {
            for (const child of children) {
              const childId = getIdStr(child);
              if (
                childrenMap.has(childId) ||
                !!isExpandableRef.current?.(child)
              ) {
                queue.push([childId, child]);
              }
            }
          }
        }

        if (
          process.env.NODE_ENV === 'development' &&
          loadCount >= LOAD_BATCH_LIMIT
        ) {
          console.warn(
            `SyutTreeGrid: Expand-all capped lazy loading at ${LOAD_BATCH_LIMIT} nodes. ` +
              `Remaining nodes will load when scrolled into view or individually expanded.`,
          );
        }
        setActiveExpanded(true);
        return;
      }

      // ── Transition FROM "expand all" ──
      if (wasAll && !isAll) {
        const newObj =
          (newExpanded as Record<string, boolean>) || {};
        for (const [id, item] of dataMap) {
          if (childrenMap.has(id) && !newObj[id]) {
            onCollapse?.(id, item);
          }
        }
        setActiveExpanded(newExpanded);
        return;
      }

      // ── Normal diff (both are Record<string, boolean>) ──
      const prevObj =
        (activeExpanded as Record<string, boolean>) || {};
      const newObj =
        (newExpanded as Record<string, boolean>) || {};

      for (const [id, isExp] of Object.entries(newObj)) {
        if (isExp && !prevObj[id]) {
          const row = dataMap.get(id);
          if (row) {
            if (
              onLoadChildren &&
              !childrenLoadedIds?.has(id) &&
              !loadingNodeIdsRef.current?.has(id)
            ) {
              onLoadChildren(id, row);
            }
            onExpand?.(id, row);
          }
        }
      }

      // Detect collapsed — key set to false OR key removed entirely
      for (const [id, wasExp] of Object.entries(prevObj)) {
        if (wasExp && !newObj[id]) {
          const row = dataMap.get(id);
          if (row) onCollapse?.(id, row);
        }
      }

      setActiveExpanded(newExpanded);
    },
    [
      activeExpanded,
      rootData,
      dataMap,
      childrenMap,
      onLoadChildren,
      onExpand,
      onCollapse,
      childrenLoadedIds,
      setActiveExpanded,
      getIdStr,
    ],
  );

  // ══════════════════════════════════════════════════════════════════════════
  // 5. Connector map — pre-computed from expanded branches
  // ══════════════════════════════════════════════════════════════════════════

  const connectorMap = useMemo(() => {
    const map = new Map<string, ConnectorInfo>();
    const expandedObj =
      activeExpanded === true
        ? null
        : (activeExpanded as Record<string, boolean>);
    const isExpanded = (id: string) =>
      expandedObj === null || !!expandedObj[id];
    const visited = new Set<string>();

    const flagStack: boolean[] = [];

    const walk = (items: T[]) => {
      items.forEach((item, index) => {
        const id = getIdStr(item);
        if (visited.has(id)) return;
        visited.add(id);
        const isLast = index === items.length - 1;
        map.set(id, {
          isLastChild: isLast,
          parentIsLastFlags: flagStack.slice(),
        });
        const children = childrenMap.get(id);
        if (children?.length && isExpanded(id)) {
          flagStack.push(isLast);
          walk(children);
          flagStack.pop();
        }
      });
    };

    walk(rootData);
    return map;
  }, [rootData, childrenMap, getIdStr, activeExpanded]);

  // ══════════════════════════════════════════════════════════════════════════
  // 6. Tree column cell renderer
  // ══════════════════════════════════════════════════════════════════════════

  const enrichedColumns = useMemo(() => {
    return columns.map((col) => {
      if (!col.isTreeColumn) return col as SyutGridColumn<T>;
      return {
        ...col,
        cell: (info: { row: Row<T>; getValue: () => any }) => {
          const row = info.row;
          const id = getIdStr(row.original);
          const meta = connectorMap.get(id);
          const isLoadingNode = loadingNodeIdsRef.current?.has(id);

          const canExpand =
            row.getCanExpand() ||
            !!isExpandableRef.current?.(row.original);

          return (
            <div className="stg-tree-cell">
              {showConnectorLines && meta && row.depth > 0 &&
                renderConnectors(meta)}
              {isLoadingNode ? (
                <span className="stg-spinner" />
              ) : canExpand ? (
                <button
                  className={`stg-chevron ${row.getIsExpanded() ? 'expanded' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    row.getToggleExpandedHandler()();
                  }}
                >
                  {IconSystem.chevronRight({ size: 14 })}
                </button>
              ) : (
                <span className="stg-chevron-spacer" />
              )}
              {showNodeIcons && renderNodeIcon(row, canExpand)}
              {col.cell ? col.cell(info) : info.getValue()}
            </div>
          );
        },
      } as SyutGridColumn<T>;
    });
  }, [columns, connectorMap, showConnectorLines, showNodeIcons, getIdStr]);

  // ══════════════════════════════════════════════════════════════════════════
  // 7. Dev warning: pagination + large visible subtree
  // ══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development' || !enablePagination) return;
    const VISIBLE_CHILD_WARN_THRESHOLD = 100;

    const expandedObj =
      activeExpanded === true
        ? null
        : (activeExpanded as Record<string, boolean>);
    const isExp = (id: string) => expandedObj === null || !!expandedObj[id];

    for (const root of rootData) {
      const rootId = getIdStr(root);
      if (!isExp(rootId)) continue;

      let count = 0;
      const countVisible = (parentId: string) => {
        const children = childrenMap.get(parentId);
        if (!children) return;
        for (const child of children) {
          count++;
          if (count > VISIBLE_CHILD_WARN_THRESHOLD) return;
          const childId = getIdStr(child);
          if (isExp(childId)) countVisible(childId);
        }
      };
      countVisible(rootId);

      if (count > VISIBLE_CHILD_WARN_THRESHOLD) {
        console.warn(
          `SyutTreeGrid: Root "${rootId}" has ${count}+ visible descendants. ` +
            `With root-level pagination, this creates long pages. ` +
            `Consider limiting expansion depth or using virtualization (Phase 2).`,
        );
        break;
      }
    }
  }, [activeExpanded, rootData, childrenMap, getIdStr, enablePagination]);

  // ══════════════════════════════════════════════════════════════════════════
  // 8. Normalize rowActions and render SyutGrid
  // ══════════════════════════════════════════════════════════════════════════

  const normalizedRowActions = useMemo(() => {
    if (!rowActions) return undefined;
    if (Array.isArray(rowActions)) return () => rowActions;
    return rowActions;
  }, [rowActions]);

  return (
    <SyutGrid<T>
      data={rootData}
      columns={enrichedColumns}
      enableExpanding={true}
      expanded={activeExpanded}
      onExpandedChange={handleExpandedChange}
      getSubRows={getSubRowsFn}
      getRowCanExpand={(row) => childrenMap.has(getIdStr(row.original)) || !!isExpandableRef.current?.(row.original)}
      getRowId={getIdStr}
      enableSorting={enableSorting ?? false}
      rowActions={normalizedRowActions}
      enableRowSelection={enableRowSelection}
      onSelectionChange={onSelectionChange}
      enablePagination={enablePagination}
      pageSizeOptions={pageSizeOptions}
      defaultPageSize={defaultPageSize}
      isLoading={isLoading}
      emptyMessage={emptyMessage}
      className={`syut-tree-grid ${className || ''}`}
      visibleColumns={visibleColumns}
      frozenLeftColumns={frozenLeftColumns}
      frozenRightColumns={frozenRightColumns}
    />
  );
}
